// DoctorEarningsCard.tsx
import React, { useState } from "react";
import RevenueService from "../../../services/revenueService";
import { DoctorEarningsCardProps } from "../types/revenue.types";
export const DoctorEarningsCard: React.FC<DoctorEarningsCardProps> = ({
  doctor,
  colorTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        marginBottom: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
      }}
    >
      {/* Doctor Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: colorTheme.gray900,
              margin: "0 0 4px 0",
            }}
          >
            👨‍⚕️ {doctor.doctor_name}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: colorTheme.gray600,
              margin: "0 0 8px 0",
            }}
          >
            {doctor.specialization}
          </p>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span style={{ color: colorTheme.gray500 }}>
              {doctor.transactions_count} transactions
            </span>
            <span style={{ color: colorTheme.gray500 }}>
              Last payment:{" "}
              {doctor.last_payment_date
                ? new Date(doctor.last_payment_date).toLocaleDateString()
                : "No payments yet"}
            </span>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: colorTheme.gray900,
            }}
          >
            {RevenueService.formatCurrency(doctor.total_earned)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: RevenueService.getGrowthColor(doctor.growth_percentage),
              fontWeight: "500",
            }}
          >
            {RevenueService.formatPercentage(doctor.growth_percentage)} vs last
            period
          </div>
          {doctor.pending_amount > 0 && (
            <div
              style={{
                fontSize: "12px",
                color: colorTheme.warning,
                fontWeight: "600",
                marginTop: "4px",
              }}
            >
              Pending: {RevenueService.formatCurrency(doctor.pending_amount)}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "16px",
          padding: "16px",
          background: colorTheme.gray50,
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.primary,
            }}
          >
            {RevenueService.formatCurrency(doctor.avg_per_transaction)}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Avg per Transaction
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.success,
            }}
          >
            {doctor.transactions_count}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Total Services
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.warning,
            }}
          >
            {doctor.services_provided.length}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Service Types
          </div>
        </div>
      </div>

      {/* Services Breakdown Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          padding: "12px",
          background: expanded ? colorTheme.primary : colorTheme.gray100,
          color: expanded ? "white" : colorTheme.gray700,
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          transition: "all 0.3s ease",
        }}
      >
        {expanded ? "Hide" : "Show"} Service Breakdown {expanded ? "▲" : "▼"}
      </button>

      {/* Expanded Services Details */}
      {expanded && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: colorTheme.gray50,
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Services Provided
          </h4>
          {doctor.services_provided.map((service: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index < doctor.services_provided.length - 1
                    ? `1px solid ${colorTheme.gray200}`
                    : "none",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: colorTheme.gray900,
                  }}
                >
                  {service.name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: colorTheme.gray600,
                  }}
                >
                  {service.count} times •{" "}
                  {RevenueService.formatCurrency(
                    service.earned / service.count,
                  )}{" "}
                  per service
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.success,
                  }}
                >
                  {RevenueService.formatCurrency(service.earned)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.success,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          💰 Process Payment
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📊 View Details
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.gray200,
            color: colorTheme.gray700,
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📧 Send Report
        </button>
      </div>
    </div>
  );
};
